import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";
import ClientTable from "./ClientForm/ClientTable";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const mapBackendListToPersonas = (data) => {
  const list = data?.personas || [];
  return list.map((p) => ({
    id: p.id,
    tipo: p.tipo || "N",
    razon_social: p.razon_social || "",
    nombre_comercial: p.nombre_comercial || "",
    cedula: p.cedula || "",
    ruc: p.ruc || "",
    telefono: p.telefono || "",
    correo: p.correo || "",
    direccion: p.direccion || "",
    es_cliente: !!p.es_cliente,
    es_proveedor: !!p.es_proveedor,
    es_empleado: !!p.es_empleado,
    es_extranjero: !!p.es_extranjero,
  }));
};

const AdminClient = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [personas, setPersonas] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rolFilter, setRolFilter] = useState("todos");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const s = location.state?.snackbar;
    if (s?.message) {
      setSnackbar({
        open: true,
        type: s.type || "success",
        message: s.message,
      });
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const res = await fetch("/clientes/listar/", { credentials: "include" });
      const data = await res.json();
      setPersonas(mapBackendListToPersonas(data));
    } catch {
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const f = personas.filter((p) => {
      const matchSearch =
        p.razon_social.toLowerCase().includes(term) ||
        p.nombre_comercial.toLowerCase().includes(term) ||
        p.cedula.toLowerCase().includes(term) ||
        p.ruc.toLowerCase().includes(term) ||
        p.telefono.toLowerCase().includes(term) ||
        p.correo.toLowerCase().includes(term);

      const matchRol =
        rolFilter === "todos" ||
        (rolFilter === "cliente" && p.es_cliente) ||
        (rolFilter === "proveedor" && p.es_proveedor) ||
        (rolFilter === "empleado" && p.es_empleado);

      return matchSearch && matchRol;
    });

    setFiltered(f);
    setPage(0);
  }, [searchTerm, rolFilter, personas]);

  const handleDelete = async () => {
    try {
      const res = await fetch(`/clientes/eliminar/${deleteId}/`, {
        method: "DELETE",
        headers: { "X-CSRFToken": getCSRFToken() },
        credentials: "include",
      });

      if (res.ok) {
        setSnackbar({
          open: true,
          type: "success",
          message: "Persona eliminada correctamente.",
        });
        fetchPersonas();
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: "No se pudo eliminar la persona.",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error al eliminar la persona.",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    const payload = {
      tipo: String(form.get("tipo")).toUpperCase(),
      razon_social: String(form.get("razon_social")).toUpperCase(),
      nombre_comercial: String(form.get("nombre_comercial")).toUpperCase(),
      cedula: String(form.get("cedula")).trim(),
      ruc: String(form.get("ruc")).trim(),
      telefono: String(form.get("telefono")).trim(),
      correo: String(form.get("correo")).trim(),
      direccion: String(form.get("direccion")).trim(),
      es_cliente: form.get("es_cliente") === "on",
      es_proveedor: form.get("es_proveedor") === "on",
      es_empleado: form.get("es_empleado") === "on",
      es_extranjero: form.get("es_extranjero") === "on",
    };

    try {
      const res = await fetch(`/clientes/editar/${selected.id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSnackbar({
          open: true,
          type: "success",
          message: "Persona actualizada correctamente.",
        });
        setEditModalOpen(false);
        fetchPersonas();
      } else {
        setSnackbar({
          open: true,
          type: "error",
          message: "No se pudo actualizar la persona.",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error al editar la persona.",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
              }}
              onClick={() => navigate("/admin-panel/clientes/nuevo")}
            >
              Nueva Persona
            </Button>
          </Box>

          <ClientTable
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            rolFilter={rolFilter}
            setRolFilter={setRolFilter}
            filtered={filtered}
            loading={loading}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            handleEdit={(p) => {
              setSelected(p);
              setEditModalOpen(true);
            }}
            handleDeleteConfirm={setDeleteId}
          />
        </Box>
      </Box>

      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Box
          component="form"
          onSubmit={handleEditSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 520,
          }}
        >
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select name="tipo" defaultValue={selected.tipo}>
                    <MenuItem value="N">Natural</MenuItem>
                    <MenuItem value="J">Jurídica</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormGroup row>
                  <FormControlLabel
                    control={<Checkbox name="es_cliente" defaultChecked={selected.es_cliente} />}
                    label="Cliente"
                  />
                  <FormControlLabel
                    control={<Checkbox name="es_proveedor" defaultChecked={selected.es_proveedor} />}
                    label="Proveedor"
                  />
                  <FormControlLabel
                    control={<Checkbox name="es_empleado" defaultChecked={selected.es_empleado} />}
                    label="Empleado"
                  />
                  <FormControlLabel
                    control={<Checkbox name="es_extranjero" defaultChecked={selected.es_extranjero} />}
                    label="Extranjero"
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="razon_social"
                  label="Razón social / Nombre"
                  defaultValue={selected.razon_social}
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="nombre_comercial"
                  label="Nombre comercial"
                  defaultValue={selected.nombre_comercial}
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField name="cedula" label="Cédula" defaultValue={selected.cedula} fullWidth />
              </Grid>

              <Grid item xs={6}>
                <TextField name="ruc" label="RUC" defaultValue={selected.ruc} fullWidth />
              </Grid>

              <Grid item xs={6}>
                <TextField name="telefono" label="Teléfono" defaultValue={selected.telefono} fullWidth />
              </Grid>

              <Grid item xs={6}>
                <TextField name="correo" label="Correo" defaultValue={selected.correo} fullWidth />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="direccion"
                  label="Dirección"
                  defaultValue={selected.direccion}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} textAlign="right">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                >
                  Guardar Cambios
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 350,
          }}
        >
          <Typography variant="h6" mb={2}>
            ¿Eliminar persona?
          </Typography>
          <Typography mb={3}>Esta acción no se puede deshacer.</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="contained"
              color="error"
              sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, textTransform: "none" }}
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
              onClick={handleDelete}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminClient;
